import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, Check } from 'lucide-react';

const NARRATORIA_KINDLE_SENDER = 'arc@narratoria.com';

/**
 * Dialog shown when an author is about to take an action (Approve / Send ARC)
 * for a reader whose profile has no Kindle email. Non-blocking — author can
 * proceed if they want. Supports either single (readerName) or bulk
 * (readerNames array) variants.
 *
 * If onProceed is omitted, only a Cancel button is rendered (e.g. "all
 * selected readers missing Kindle, nothing to send to").
 */
export default function KindleMissingDialog({
  open,
  readerName,
  readerNames,
  onCancel,
  onProceed,
  proceedLabel = 'Proceed anyway',
}) {
  const names = Array.isArray(readerNames) ? readerNames.filter(Boolean) : [];
  const isPlural = names.length > 1;
  const [copied, setCopied] = useState(false);

  async function copySenderEmail() {
    try {
      await navigator.clipboard.writeText(NARRATORIA_KINDLE_SENDER);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text on a hidden input
      const ta = document.createElement('textarea');
      ta.value = NARRATORIA_KINDLE_SENDER;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel?.(); }}>
      <DialogContent className="max-w-lg rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            {isPlural ? 'Some readers have no Kindle email' : 'Reader has no Kindle email'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isPlural ? (
            <div className="text-sm text-slate-600">
              <p className="mb-2">{names.length} readers haven't added a Kindle email to their profile:</p>
              <ul className="ml-4 list-disc text-slate-700">
                {names.slice(0, 8).map((n, i) => <li key={i}>{n}</li>)}
                {names.length > 8 && <li className="italic text-slate-500">…and {names.length - 8} more</li>}
              </ul>
            </div>
          ) : readerName ? (
            <p className="text-sm text-slate-600">
              <strong>{readerName}</strong> hasn't added a Kindle email to their profile yet.
            </p>
          ) : null}
          <p className="text-sm text-slate-700 leading-relaxed">
            This reader doesn't have a Kindle email yet. Ask them to go to their Profile in
            the reader portal, add their Kindle email, AND remember to add{' '}
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[13px] text-slate-900 border border-slate-200">
              {NARRATORIA_KINDLE_SENDER}
              <button
                type="button"
                onClick={copySenderEmail}
                className="ml-1 inline-flex items-center justify-center rounded-md p-1 text-slate-500 hover:text-teal-600 hover:bg-white"
                title="Copy email"
                aria-label="Copy arc@narratoria.com to clipboard"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </span>{' '}
            to their Kindle verified senders list on the Amazon desktop dashboard (not the mobile
            app — the app doesn't have this option).
          </p>
          {copied && (
            <p className="text-xs text-green-700">Copied {NARRATORIA_KINDLE_SENDER} to clipboard.</p>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className={onProceed ? 'flex-1' : 'w-full'}
              onClick={onCancel}
            >
              {onProceed ? 'Cancel' : 'Close'}
            </Button>
            {onProceed && (
              <Button
                className="flex-1 bg-gradient-to-r from-slate-500 to-teal-600 font-semibold text-white hover:from-teal-600 hover:to-teal-700"
                onClick={onProceed}
              >
                {proceedLabel}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
